import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  BoxProps,
  Button,
  Center,
  ChakraProps,
  HStack,
  Icon,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import { CHUNK_H, CHUNK_W } from "../../models/api/location";
import SelectRoom from "./SelectRoom";
import { MdLocationPin } from "react-icons/md";
import ImageFrame from "./ImageFrame";
import { ChunkWithId, Door } from "@worldwidewebb/client-world";
import ReactFlow, { Node, NodeProps, ReactFlowProvider, SelectionMode, useNodesState, useReactFlow } from "reactflow";
import Background from "../../export/reactflow/components/base/Background";
import Controls from "../../export/reactflow/components/base/Controls";
import MiniMap from "../../export/reactflow/components/base/MiniMap";
import { ulid } from "ulid";
import { useToken } from "@chakra-ui/system";
import { DoorData } from "../reactFlow/nodeTypes/DoorNode";
import useDoors from "../../hooks/doors/useDoors";
import useRoom from "../../hooks/rooms/useRoom";

// TODO: VERY WIP AND DIRTY

const BackgroundNode: React.FC<NodeProps> = ({ data: { chunks } }) => {
  return <LocationRender chunks={chunks} />;
};

const LocationNode: React.FC<NodeProps> = ({ data: { color } }) => {
  return <Box bg={color} color={color} p={2}></Box>;
};

const nodeTypes = {
  LocationNode,
  BackgroundNode,
};

interface LocationCanvasProps extends ChakraProps {
  doors: Door[];
  chunks: ChunkWithId[];
  roomId: string;
  doorId: string;
  setDoorId: (doorId: string) => void;
}

const LocationCanvas: React.FC<LocationCanvasProps> = ({ doors, chunks, roomId, doorId: canvasDoorId, setDoorId }) => {
  const [colorRed, colorGreen] = useToken("colors", ["red.800", "green.600"]);

  const nodeColors: Record<string, string> = {
    "red.800": colorRed,
    "green.600": colorGreen,
  };

  const coordinates = useMemo(
    () =>
      chunks.flatMap(({ layers, coordinates }) =>
        layers.map(({ dx, dy }) => ({ x: coordinates[0] * CHUNK_W + dx, y: coordinates[1] * CHUNK_H + dy }))
      ),
    [chunks]
  );

  const { w, h } = useMemo(() => {
    const minX = Math.min(...coordinates.map(({ x }) => x));
    const maxX = Math.min(...coordinates.map(({ x }) => x + CHUNK_W));

    const minY = Math.min(...coordinates.map(({ y }) => y));
    const maxY = Math.min(...coordinates.map(({ y }) => y + CHUNK_H));

    return {
      w: maxX - minX,
      h: maxY - minY,
    };
  }, [coordinates]);

  const radius = 16;

  const backgroundNode: Node = {
    id: ulid(),
    type: "BackgroundNode",
    data: {
      chunks,
    },
    position: { x: 0, y: 0 },
    style: {
      width: w,
      height: h,
      zIndex: 0,
    },
    selectable: false,
    draggable: false,
  };

  const doorNodes: Node[] = useMemo(
    () =>
      doors.map(({ doorId, worldId, coordinates }) => ({
        id: ulid(),
        type: "LocationNode",
        data: {
          color: doorId === canvasDoorId && worldId === roomId ? "green.600" : "red.800",
          nodeData: {
            doorId,
            worldId,
          },
        },
        position: { x: coordinates[0], y: coordinates[1] },
        style: {
          width: radius * 2,
          height: radius * 2,
          zIndex: 1,
        },
        selectable: true,
        draggable: false,
      })),
    [doors, canvasDoorId, roomId]
  );

  const [nodes, setNodes, onNodesChange] = useNodesState([backgroundNode]);

  useEffect(() => {
    setNodes((nodes) => [...nodes.filter(({ type }) => type === "BackgroundNode"), ...doorNodes]);
  }, [doorNodes]);

  const handleNodeClick = useCallback(
    (node: Node) => {
      if (node.type === "BackgroundNode") {
        return;
      }

      const {
        data: {
          nodeData: { doorId: selectedDoorId },
        },
      } = node;

      setNodes((nodes) =>
        nodes.map((node) => {
          if (node.type === "BackgroundNode") {
            return node;
          }

          const {
            data: {
              nodeData: { doorId, worldId },
            },
          } = node;

          node.data.color = doorId === selectedDoorId && worldId === roomId ? "green.600" : "red.800";

          return node;
        })
      );

      setDoorId(selectedDoorId);
    },
    [setDoorId, roomId]
  );

  return (
    <ReactFlow
      nodes={nodes}
      nodeTypes={nodeTypes}
      onNodesChange={onNodesChange}
      proOptions={{ hideAttribution: true }}
      multiSelectionKeyCode={["Meta", "Control"]}
      deleteKeyCode={["Backspace", "Delete"]}
      selectionKeyCode={null}
      selectionMode={SelectionMode.Partial}
      panOnDrag={[1]}
      panActivationKeyCode={"Shift"}
      onNodeClick={(_, node) => handleNodeClick(node)}
      selectionOnDrag={true}
      minZoom={0.125}
      maxZoom={2}
    >
      <Background />
      <Controls position={"top-right"} showInteractive={false} />
      <MiniMap
        position={"bottom-right"}
        pannable={true}
        zoomable={true}
        nodeColor={({ type, data: { color } }) => (type === "LocationNode" ? nodeColors[color] : "transparent")}
      />
    </ReactFlow>
  );
};

interface LocationRenderProps extends BoxProps {
  chunks: ChunkWithId[];
}

const LocationRender: React.FC<LocationRenderProps> = ({ chunks, children }) => {
  return (
    <Box position={"relative"}>
      <>
        {chunks.flatMap(({ layers, coordinates }, index$1) =>
          layers.map((layer, index$2) => {
            const { imageData: src, frameTimes, dx, dy, dz } = layer;
            const top = `${coordinates[1] * CHUNK_H + dy}px`;
            const left = `${coordinates[0] * CHUNK_W + dx}px`;
            const zIndex = -dz;

            return (
              <ImageFrame
                key={`${index$1} ${index$2}`}
                src={src}
                frameTimes={frameTimes}
                position={"absolute"}
                top={top}
                left={left}
                zIndex={zIndex}
              />
            );
          })
        )}

        {children}
      </>
    </Box>
  );
};

interface DoorPickerProps extends ChakraProps {
  isOpen: boolean;
  onClose: () => void;
  roomId: string;
  doorId: string;
  setDoorId: (doorName: string) => void;
}

const DoorPicker: React.FC<DoorPickerProps> = ({ color, isOpen, onClose, roomId, doorId, setDoorId }) => {
  const { chunks } = useRoom(roomId);
  const { doors = [] } = useDoors(roomId);

  const [currentDoorId, setCurrentDoorId] = useState<string>(doorId);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);

  const reactFlow = useReactFlow();

  const handleUpdate = useCallback(() => {
    setDoorId(currentDoorId);

    onClose();
  }, [currentDoorId]);

  useEffect(() => {
    setTimeout(() => reactFlow.fitView(), 100);
  }, [reactFlow]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={"full"}>
      <ModalOverlay />

      <ModalContent bg={"theme.dark.background"}>
        <ModalHeader>
          <Text color={color} casing={"uppercase"}>
            Select a location
          </Text>
        </ModalHeader>

        <ModalCloseButton color={color} />

        <ModalBody display={"flex"}>
          <Box
            flexGrow={1}
            borderColor={color}
            borderWidth={1}
            sx={{
              "::-webkit-scrollbar": {
                width: 2,
                height: 2,
                bg: "transparent",
              },
              "::-webkit-scrollbar-track": {
                bg: "transparent",
              },
              "::-webkit-scrollbar-thumb": {
                bg: color,
              },
              "::-webkit-scrollbar-corner": {
                bg: color,
              },
            }}
            overflow={"auto"}
          >
            {isLoading && (
              <Center w={"100%"} h={"100%"}>
                <Spinner color={color?.toString() ?? ""} />
              </Center>
            )}
            {hasError && (
              <Center w={"100%"} h={"100%"}>
                <Text color={color} casing={"uppercase"}>
                  Failed to load world information
                </Text>
              </Center>
            )}
            {!isLoading && !hasError && (
              <LocationCanvas
                color={color}
                doors={doors}
                chunks={chunks}
                roomId={roomId}
                doorId={doorId}
                setDoorId={setCurrentDoorId}
              />
            )}
          </Box>
        </ModalBody>

        <ModalFooter>
          <HStack>
            <Button onClick={onClose} variant={"outline"} borderColor={color} borderRadius={0}>
              <Text color={color} casing={"uppercase"}>
                Cancel
              </Text>
            </Button>
            <Button onClick={handleUpdate} variant={"outline"} borderColor={color} borderRadius={0}>
              <Text color={color} casing={"uppercase"}>
                Update
              </Text>
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

interface SelectDoorProps extends ChakraProps {
  value: DoorData | undefined;
  setValue: (value: DoorData) => void;
}

const SelectDoor: React.FC<SelectDoorProps> = ({ color, value, setValue, ...chakraProps }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [roomName, setRoomName] = useState<string>(value?.worldId ?? "");
  const [doorName, setDoorName] = useState<string>(value?.doorId ?? "");

  useEffect(() => {
    setValue({
      doorId: doorName,
      worldId: roomName,
    });
  }, [doorName, roomName]);

  return (
    <ReactFlowProvider>
      <Stack {...chakraProps}>
        <HStack alignItems={"end"}>
          <SelectRoom
            value={roomName}
            setValue={(value) => setRoomName(value)}
            color={color}
            flexGrow={1}
            flexShrink={0}
          />

          {roomName !== "" && (
            <>
              <Tooltip
                bg={"theme.dark.background"}
                borderColor={color}
                borderRadius={0}
                borderWidth={1}
                color={color}
                label={"select door"}
                placement={"right"}
              >
                <IconButton
                  borderColor={color}
                  borderRadius={0}
                  borderWidth={2}
                  color={color}
                  variant={"outline"}
                  icon={<Icon as={MdLocationPin} />}
                  aria-label={"select door"}
                  onClick={onOpen}
                />
              </Tooltip>

              <DoorPicker
                color={color}
                isOpen={isOpen}
                onClose={onClose}
                roomId={roomName}
                doorId={doorName}
                setDoorId={setDoorName}
              />
            </>
          )}
        </HStack>
      </Stack>
    </ReactFlowProvider>
  );
};

export default SelectDoor;
